import React, { useState, useEffect } from "react";
import { Check, Lock } from "lucide-react";
import { routes as modules } from "../../appConfig/PermissionModule";

const actions = [
  { id: "view", name: "View", description: "Can view items" },
  { id: "add", name: "Create", description: "Can create items" },
  { id: "update", name: "Edit", description: "Can edit items" },
  { id: "delete", name: "Delete", description: "Can delete items" },
];

const PermissionMatrix = ({
  permissions = [],        // incoming “granted” perms
  onChange,
  readOnly = false,
  isSystemRole = false,
}) => {
  const initializePermissions = () => {
    return modules.map((mod) => {
      // find incoming grant for this module, if any
      const incoming = permissions.find((p) => p.key === mod.key);
      const scopes = actions.map((act) => {
        const incomingScope = incoming?.scopes.find((s) => s.key === act.id);
        return { key: act.id, value: incomingScope ? incomingScope.value : false };
      });
      return { key: mod.key, scopes };
    });
  };

  const [selectedPermissions, setSelectedPermissions] = useState(
    initializePermissions()
  );

  useEffect(() => {
    setSelectedPermissions(initializePermissions());
  }, [permissions]);

  const isPermissionSelected = (key, action) => {
    const perm = selectedPermissions.find((p) => p.key === key);
    return !!perm?.scopes.find((s) => s.key === action)?.value;
  };

  // same builder as before
  const buildChangedPermissions = (updated) => {
    return updated
      .map((perm) => {
        const mod = modules.find((m) => m.key === perm.key);
        if (!mod) return null;
        const granted = perm.scopes.filter((s) => s.value);
        if (granted.length === 0) return null;
        return {
          module_name: mod.module_name,
          module_slug: mod.module_slug,
          key: mod.key,
          scopes: granted,
          permission_key: mod.permission_key || [],
        };
      })
      .filter(Boolean);
  };

  const togglePermission = (key, action) => {
    if (readOnly) return;
    if (isSystemRole && isPermissionSelected(key, action)) return;

    setSelectedPermissions((prev) => {
      const updated = prev.map((perm) => {
        if (perm.key !== key) return perm;
        const newScopes = perm.scopes.map((s) =>
          s.key === action ? { ...s, value: !s.value } : s
        );
        if (
          ["add", "update", "delete"].includes(action) &&
          !newScopes.find((s) => s.key === "view")?.value
        ) {
          newScopes.forEach((s, i) => {
            if (s.key === "view") newScopes[i] = { ...s, value: true };
          });
        }
        return { ...perm, scopes: newScopes };
      });
      onChange?.(buildChangedPermissions(updated));
      return updated;
    });
  };

  const toggleAllModulePermissions = (key) => {
    if (readOnly) return;

    setSelectedPermissions((prev) => {
      const updated = prev.map((perm) => {
        if (perm.key !== key) return perm;
        const allOn = perm.scopes.every((s) => s.value);
        const newScopes = perm.scopes.map((s) => ({
          ...s,
          value: !allOn,
        }));
        if (!allOn) {
          const idx = newScopes.findIndex((s) => s.key === "view");
          if (idx >= 0) newScopes[idx] = { ...newScopes[idx], value: true };
        }
        return { ...perm, scopes: newScopes };
      });
      onChange?.(buildChangedPermissions(updated));
      return updated;
    });
  };

  const getCellClasses = (key, action) => {
    const selected = isPermissionSelected(key, action);
    const disabled = readOnly || (isSystemRole && selected);
    return `
      flex items-center justify-center w-full h-full
      ${selected ? "bg-primary-50 text-primary border-primary" : "bg-white text-gray-500 border-gray-200"}
      ${disabled ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-gray-50"}
      border rounded-md p-2 transition-colors
    `;
  };

  return (
    <div className="w-full overflow-x-auto h-full">
      <table className="w-full border-collapse">
        <thead>
          <tr>
            <th className="w-1/4 px-4 py-2 text-left text-sm font-medium text-gray-500">
              Module
            </th>
            {actions.map((a) => (
              <th key={a.id} className="px-2 py-2 text-center">
                {a.name}
                <div className="text-xs text-gray-400">{a.description}</div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {modules.map(
            (mod) =>
              mod.isProtected && (
                <tr key={mod.key}>
                  <td
                    className="px-4 py-3 text-sm text-gray-700 cursor-pointer"
                    onClick={() => toggleAllModulePermissions(mod.key)}
                  >
                    {mod.module_name}
                  </td>
                  {actions.map((a) => (
                    <td key={a.id} className="px-2 py-2 text-center">
                      <div
                        className={getCellClasses(mod.key, a.id)}
                        onClick={() => togglePermission(mod.key, a.id)}
                      >
                        {isPermissionSelected(mod.key, a.id) ? (
                          isSystemRole ? (
                            <Lock className="h-4 w-4" />
                          ) : (
                            <Check className="h-4 w-4" />
                          )
                        ) : (
                          <span className="h-4 w-4 block" />
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              )
          )}
        </tbody>
      </table>

      {readOnly && (
        <p className="mt-4 text-sm italic text-gray-500">
          Permissions are read-only in view mode.
        </p>
      )}
      {isSystemRole && (
        <p className="mt-4 text-sm italic text-gray-500">
          <Lock className="inline h-4 w-4 mr-1" />
          Locked permissions cannot be removed from system roles.
        </p>
      )}
    </div>
  );
};

export default PermissionMatrix;
